import { computed, inject, Injectable, signal } from '@angular/core';
import { debounceTime, Subject, Subscription, tap } from 'rxjs';

import { SearchRequestModel } from '~app/models/requests/search-request.model';
import { ColumnFilterModel } from '~core/models/column-filter.model';
import { ColumnSortModel } from '~core/models/column-sort.model';
import { ComparisonOperatorsEnumExtensions } from '~core/models/enums/comparison-operators.enum';
import { OperandTypeEnum } from '~core/models/enums/operand-type.enum';
import { SortDirectionEnum } from '~core/models/enums/sort-direction.enum';
import { SearchRequestServiceConfigModel } from '~core/models/search-request-service-config.model';
import { Utils } from '~core/utils/tools/utils.tools';
import { QueryParamSyncService } from './query-param-sync.service';

@Injectable({
    providedIn: 'root'
})
export class SearchRequestService {
    static readonly MAX_SEARCH_RESULTS = 500_000;

    private static readonly searchRequestDebounceTimeMs = 250;

    readonly isLoadingSearch = signal(false);
    readonly isLoadingCount = signal(false);
    readonly totalRecords = signal(-1);
    readonly isSearchResultCapHit = computed(() => this.totalRecords() === SearchRequestService.MAX_SEARCH_RESULTS);

    get isUrlSyncEnabled(): boolean {
        return this.config?.shouldSyncWithUrl === true;
    }

    readonly current = computed(() => this.internalSearchRequest(), { equal: Utils.areEqual });

    /**
     * same as {@link current} but properties are separated computed's.
     */
    readonly currentProperties = {
        searchText: computed(() => this.internalSearchRequest().searchText),
        pageIndex: computed(() => this.internalSearchRequest().pageIndex),
        pageSize: computed(() => this.internalSearchRequest().pageSize),
        sortings: computed(() => this.internalSearchRequest().sortings, { equal: Utils.areEqual }),
        filters: computed(() => this.internalSearchRequest().filters, { equal: Utils.areEqual }),
        id: computed(() => this.internalSearchRequest().id)
    } as const;

    private internalSearchRequest = signal<SearchRequestModel>(
        {
            sortings: [],
            filters: []
        },
        { equal: Utils.areEqual }
    );

    private config?: SearchRequestServiceConfigModel;

    private searchRequestLoadingStartSubscription?: Subscription;
    private searchRequestCountingStartSubscription?: Subscription;

    private currentSearchSubscription?: Subscription;
    private currentCountingSubscription?: Subscription;

    private readonly triggerDebouncedSearchSub = new Subject<SearchRequestModel>();
    private readonly triggerDebouncedCountSub = new Subject<SearchRequestModel>();

    private readonly queryParamSynchronizer = inject(QueryParamSyncService).createQueryParamSynchronizer<SearchRequestModel>(
        [
            { property: 'searchText', type: 'string' },
            {
                property: 'filters',
                type: 'object',
                customSerializer: {
                    serialize: (value: ColumnFilterModel[]) => this.serializeFilters(value || []),
                    deserialize: value => this.deserializeFilters(value || '')
                }
            },
            {
                property: 'sortings',
                type: 'object',
                customSerializer: {
                    serialize: (value: ColumnSortModel[]) => this.serializeSortings(value || []),
                    deserialize: value => this.deserializeSortings(value || '')
                }
            },
            { property: 'pageIndex', type: 'number' },
            { property: 'pageSize', type: 'number' },
            { property: 'id', queryParamName: 'savedFilterId', type: 'string' }
        ],
        data => {
            if (!Utils.areEqual(this.internalSearchRequest(), data)) {
                const searchRequestChanges: Partial<SearchRequestModel> = {
                    searchText: data.searchText,
                    sortings: data.sortings || [],
                    filters: data.filters || [],
                    id: data.id
                };

                if (data.pageSize !== undefined) {
                    searchRequestChanges.pageSize = data.pageSize;
                }

                if (data.pageIndex !== undefined) {
                    searchRequestChanges.pageIndex = data.pageIndex;
                }

                this.setSearchRequestPartial(searchRequestChanges);
            }
        }
    );

    /**
     * Initialize the service with the given configuration.
     */
    init(config: SearchRequestServiceConfigModel): void {
        this.config = config;

        // handler for debounced searching
        this.searchRequestLoadingStartSubscription?.unsubscribe();
        this.searchRequestLoadingStartSubscription = this.triggerDebouncedSearchSub
            .pipe(debounceTime(SearchRequestService.searchRequestDebounceTimeMs))
            .subscribe(searchRequest => {
                // cancel previous request
                this.currentSearchSubscription?.unsubscribe();
                this.currentSearchSubscription = config
                    .performSearch(searchRequest)
                    .pipe(
                        tap({
                            // don't use finalize, because it would be called on manual unsubscribe too
                            next: () => this.isLoadingSearch.set(false),
                            error: () => this.isLoadingSearch.set(false),
                            complete: () => this.isLoadingSearch.set(false)
                        })
                    )
                    .subscribe();
            });

        // handler for debounced counting
        this.searchRequestCountingStartSubscription?.unsubscribe();
        this.searchRequestCountingStartSubscription = this.triggerDebouncedCountSub
            .pipe(debounceTime(SearchRequestService.searchRequestDebounceTimeMs))
            .subscribe(searchRequest => {
                // cancel previous request
                this.currentCountingSubscription?.unsubscribe();
                this.currentCountingSubscription = config
                    .performCount({
                        sortings: [],
                        filters: searchRequest.filters,
                        searchText: searchRequest.searchText
                    })
                    .pipe(
                        tap({
                            // don't use finalize, because it would be called on manual unsubscribe too
                            next: () => this.isLoadingCount.set(false),
                            error: () => this.isLoadingCount.set(false),
                            complete: () => this.isLoadingCount.set(false)
                        })
                    )
                    .subscribe(result => this.totalRecords.set(result));
            });

        if (!!config.initialState) {
            this.setSearchRequest(config.initialState);
        }

        if (config.shouldSyncWithUrl) {
            // initializes query param synchronizer and gets initial data from URL
            this.queryParamSynchronizer.initialize();
        }

        // make sure search and count are triggered at start
        this.forceReload();
    }

    /**
     * Reset the service to its initial state.
     */
    reset(): void {
        this.searchRequestLoadingStartSubscription?.unsubscribe();
        this.searchRequestLoadingStartSubscription = undefined;
        this.searchRequestCountingStartSubscription?.unsubscribe();
        this.searchRequestCountingStartSubscription = undefined;
        this.queryParamSynchronizer.stopSync();
        this.isLoadingSearch.set(false);
        this.isLoadingCount.set(false);
        this.totalRecords.set(-1);
        this.internalSearchRequest.set(new SearchRequestModel());
        this.config = undefined;
    }

    /**
     * Update the current search request and triggers debounced search and count if something changed.
     */
    setSearchRequest(searchRequest: SearchRequestModel, options?: { shouldDisableTriggerCount: boolean }): void {
        if (!this.config) {
            console.error('SearchRequestService not initialized');
            return;
        }

        const currentValue = this.internalSearchRequest();
        const newValue = Utils.deepCopy(searchRequest);

        const isInitialCountRequest = this.totalRecords() === -1;
        const shouldRecalculateCount =
            currentValue.searchText !== newValue.searchText || !Utils.areEqual(currentValue.filters, newValue.filters, ['internalId']);

        // check if count must be executed (totalRecords may have changed)
        if ((shouldRecalculateCount || isInitialCountRequest) && !options?.shouldDisableTriggerCount) {
            this.triggerCount(newValue);

            // user should not see -1, it's just for us to know it's the initial request
            if (isInitialCountRequest) {
                this.totalRecords.set(0);
            }
        }

        // check if pageIndex should be reset to first page (if count may have changed or pageSize changed)
        // except if currentValue.pageIndex is not set or already 0, e.g. on the initial load
        if (!!currentValue.pageIndex && (shouldRecalculateCount || currentValue.pageSize !== newValue.pageSize)) {
            newValue.pageIndex = 0;
        }

        // check if search must be executed
        if (
            currentValue.searchText !== newValue.searchText
            || currentValue.pageIndex !== newValue.pageIndex
            || currentValue.pageSize !== newValue.pageSize
            || !Utils.areEqual(currentValue.filters, newValue.filters, ['internalId'])
            || !Utils.areEqual(currentValue.sortings, newValue.sortings)
        ) {
            this.triggerSearch(newValue);
        }

        this.internalSearchRequest.set(newValue);
    }

    /**
     * Partially update the current search request and triggers debounced search and count if something changed.
     */
    setSearchRequestPartial(searchRequest: Partial<SearchRequestModel>, options?: { shouldDisableTriggerCount: boolean }): void {
        const current = Utils.deepCopy(this.internalSearchRequest());
        this.setSearchRequest(
            {
                ...current,
                ...Utils.deepCopy(searchRequest)
            },
            options
        );
    }

    /**
     * Force the search and count requests to re-trigger
     */
    forceReload(options?: { disableRefreshCount: boolean }): void {
        if (!options?.disableRefreshCount) {
            this.triggerCount(this.current());
        }
        this.triggerSearch(this.current());
    }

    /**
     * serialize all filters as a string that can be used as a query param
     */
    serializeFilters(filters: ColumnFilterModel[]): string {
        return filters
            .map(
                f =>
                    `${OperandTypeEnum[f.operand]}`
                    + `|${f.column}`
                    + `|${ComparisonOperatorsEnumExtensions.toText(f.comparisonOperator)}`
                    + `|${JSON.stringify(f.values)}`
            )
            .join(';');
    }

    /**
     * deserialize filters from a query param entry
     */
    deserializeFilters(filtersParam: string): ColumnFilterModel[] {
        const filters: ColumnFilterModel[] = [];
        const serializedFilters = filtersParam.split(';');

        for (const serializedFilter of serializedFilters) {
            const [operandStr, column, comparisonOperatorStr, valuesJson] = serializedFilter.split('|');
            if (column === undefined || valuesJson === undefined) {
                continue;
            }

            const comparisonOperator = ComparisonOperatorsEnumExtensions.operatorToEnum(comparisonOperatorStr);
            const operand = OperandTypeEnum[operandStr as keyof typeof OperandTypeEnum] || OperandTypeEnum.And;

            try {
                const values = JSON.parse(valuesJson);
                if (!Array.isArray(values)) {
                    continue;
                }
                filters.push(new ColumnFilterModel({ column, values, comparisonOperator, operand }));
            } catch {
                continue;
            }
        }

        return filters;
    }

    /**
     * serialize all sortings as a string that can be used as a query param
     */
    serializeSortings(sortings: ColumnSortModel[]): string {
        return sortings.map(s => `${s.column}|${SortDirectionEnum[s.direction]}`).join(';');
    }

    /**
     * deserialize sortings from a query param entry
     */
    deserializeSortings(sortingsParam: string): ColumnSortModel[] {
        const sortings: ColumnSortModel[] = [];
        const serializedSortings = sortingsParam.split(';');

        for (const serializedSorting of serializedSortings) {
            const [column, directionStr] = serializedSorting.split('|');
            if (column === undefined) {
                continue;
            }

            const direction = SortDirectionEnum[directionStr as keyof typeof SortDirectionEnum] || SortDirectionEnum.Asc;
            sortings.push({ column, direction });
        }

        return sortings;
    }

    private triggerCount(searchRequest: SearchRequestModel): void {
        if (!this.config) {
            console.error('SearchRequestService not initialized');
            return;
        }

        // start loading now, before debounce time
        this.isLoadingCount.set(true);
        this.config.onStartLoadingCount?.();

        this.triggerDebouncedCountSub.next(searchRequest);
    }

    private triggerSearch(searchRequest: SearchRequestModel): void {
        if (!this.config) {
            console.error('SearchRequestService not initialized');
            return;
        }

        // start loading now, before debounce time
        this.isLoadingSearch.set(true);
        this.config.onStartLoadingSearch?.();

        // search request changed, update URL
        if (this.config.shouldSyncWithUrl) {
            this.queryParamSynchronizer.setData(searchRequest);
        }

        this.triggerDebouncedSearchSub.next(searchRequest);
    }
}
